import React, { useState } from 'react';

import Image from 'next/image';
import styled from 'styled-components';

import { RoomViewModel } from '@api/index';
import HorizontalScrollableList from '@components/utils/HorizontalScrollableList';
import {
  useGalleriesKeys,
  default as ImageViewer,
} from '@components/galleries/Gallery';
import { Available, AvailableLimited } from '@assets/icons';
import { HotelDetailVideoViewModel } from '@api/v3';
import { FlexibleDiv } from '@components/formStyles';
import VideoGalleryItem from '@components/galleries/Gallery/GalleryVideoItem';

import { RoomFeatures } from './FeatureSetRenderers';
import { FeaturesBlock } from './styled';
import {
  Container,
  Content,
  GalleryItem,
  GalleryTitle,
  ImageContainer,
  TopMenu,
  TopMenuItem,
  ModalCloseButton,
  Legend,
  LegendItem,
  LegendTitle,
  GalleryContainerMobile,
  RoomFeaturesContainer,
  ModalTitle,
} from './DetailsGalleriesModalMobileStyles';
import {
  DetailsGalleriesModalProps,
  ModalView,
  EitherRoomOrHotelFeaturesMeta,
} from './types';

const HorizontalImageList = styled(HorizontalScrollableList)`
  width: 100%;
  padding: 0 15px;
`;
export const Gallery = ({
  title,
  images = [],
  showGallery,
  videos,
}: {
  title: string;
  images: DetailsGalleriesModalProps['galleries'][string];
  showGallery: (start: number) => void;
  videos?: HotelDetailVideoViewModel[];
  control?: boolean;
}) => (
  <>
    {title === 'Videolar' ? (
      <GalleryItem>
        <GalleryTitle>
          {title}&nbsp;({videos?.length})
        </GalleryTitle>
        <HorizontalImageList>
          {images?.map((d, idx) => (
            <ImageContainer
              key={idx}
              onClick={() => {
                showGallery(idx);
              }}
            >
              {videos?.map((video) => (
                <FlexibleDiv
                  style={{ height: 'inherit' }}
                  key={video.url}
                  bottom={30}
                >
                  <VideoGalleryItem
                    video={video}
                    gallery={true}
                    item={{ ...video, isVideo: true }}
                  />
                </FlexibleDiv>
              ))}
            </ImageContainer>
          ))}
        </HorizontalImageList>
      </GalleryItem>
    ) : (
      <GalleryItem>
        <GalleryTitle>
          {title}&nbsp;({images.length})
        </GalleryTitle>
        <HorizontalImageList>
          {images?.map((d, idx) => (
            <ImageContainer
              key={idx}
              onClick={() => {
                showGallery(idx);
              }}
            >
              <Image
                alt={d.originalAlt ?? ''}
                src={d.original}
                width={250}
                height={143}
              />
            </ImageContainer>
          ))}
        </HorizontalImageList>
      </GalleryItem>
    )}
  </>
);

type GalleryListProps = Pick<DetailsGalleriesModalProps, 'galleries'> & {
  closeGallery: () => void;
  showGallery: (key: string) => void;
  activeGallery: string;
  videos?: HotelDetailVideoViewModel[];
  control?: boolean;
};
export const GalleryList = ({
  galleries,
  closeGallery,
  activeGallery,
  showGallery,
  videos,
  control,
}: GalleryListProps) => {
  const keys = useGalleriesKeys(galleries);
  const [startImage, setStartImage] = useState(0);
  return (
    <>
      {activeGallery ? (
        <GalleryContainerMobile>
          <ImageViewer
            galleries={{ [activeGallery]: galleries[activeGallery] }}
            closeGallery={() => {
              closeGallery();
              setStartImage(0);
            }}
            title={activeGallery}
            hideCategoryAll={false}
            startAtImage={startImage}
            initialGallery={activeGallery}
            videos={videos}
            control={control}
          />
        </GalleryContainerMobile>
      ) : (
        <>
          {keys?.map((title) => (
            <Gallery
              title={title === 'all' ? 'Tümü' : title}
              images={galleries[title]}
              key={title}
              showGallery={(idx) => {
                setStartImage(idx);
                showGallery(title);
              }}
              videos={videos}
              control={control}
            />
          ))}
        </>
      )}
    </>
  );
};

const EntityDisplayTitle = {
  hotel: 'Otel',
  room: 'Oda',
};

type DetailsGalleriesModalMobileProps = React.PropsWithChildren<
  Pick<DetailsGalleriesModalProps, 'galleries' | 'closeModal'> & {
    view: ModalView;
    room?: RoomViewModel;
    videos?: HotelDetailVideoViewModel[];
    control?: boolean;
  } & EitherRoomOrHotelFeaturesMeta
>;

const DetailsGalleriesModalMobile = ({
  closeModal,
  galleries,
  view: initialView,
  room,
  videos,
  control,
  ...rest
}: DetailsGalleriesModalMobileProps) => {
  const [view, setView] = useState<ModalView>(initialView);
  const [activeGallery, setActiveGallery] = useState('');
  return (
    <Container activeGallery={activeGallery}>
      {activeGallery ? null : (
        <>
          <ModalTitle>
            {EntityDisplayTitle[rest.entityType]} Fotoğrafları
          </ModalTitle>
          {rest.entityType === 'room' ? (
            <TopMenu activeItem={view}>
              <TopMenuItem
                data-item='galleries'
                onClick={() => setView(ModalView.GALLERIES)}
              >
                {EntityDisplayTitle[rest.entityType]} Fotoğrafları
              </TopMenuItem>
              <TopMenuItem
                data-item='details'
                onClick={() => setView(ModalView.DETAILS)}
              >
                {EntityDisplayTitle[rest.entityType]} Özellikleri
              </TopMenuItem>
            </TopMenu>
          ) : null}
        </>
      )}
      <Content>
        {view === ModalView.GALLERIES ? (
          <GalleryList
            galleries={galleries}
            activeGallery={activeGallery}
            closeGallery={() => setActiveGallery('')}
            showGallery={setActiveGallery}
            videos={videos}
            control={control}
          />
        ) : (
          <>
            <Legend>
              <LegendItem>
                <Available />
                <LegendTitle>Ücretsiz olanaklar</LegendTitle>
              </LegendItem>
              <LegendItem>
                <AvailableLimited />
                <LegendTitle>Ücretli olanaklar</LegendTitle>
              </LegendItem>
            </Legend>
            <FeaturesBlock>
              {rest.entityType === 'room' ? (
                <RoomFeaturesContainer>
                  <RoomFeatures room={room} features={rest.features} />
                </RoomFeaturesContainer>
              ) : null}
            </FeaturesBlock>
          </>
        )}
      </Content>
      {activeGallery ? null : <ModalCloseButton onClick={closeModal} noLabel />}
    </Container>
  );
};

export default DetailsGalleriesModalMobile;
